// Single source of truth for messaging channels.
//
// Three different things live here — keep them apart:
//   • id    — the numeric channel id the backend uses (msg_channel_id, channel_id,
//             selectedChannelIds, customer_channel). Prefer looking channels up by id.
//   • name  — the EXACT channel name string the backend stores and matches on
//             (selectedChannels in message_combine, preferred_channel, MsgChannel.name,
//             ChannelTabsFixed panel keys). NEVER change these — saved templates and
//             API calls depend on them.
//   • label — what the UI shows. Safe to change at any time (e.g. In-App → "Inbox").
//
// /channel/list only returns { id, name }; run its rows through getChannelLabel() (or
// withChannelMeta()) before displaying them so the UI label is used instead.
import { LuBell, LuInbox, LuMail, LuMessageCircle, LuMessageSquare, LuPhone } from 'react-icons/lu';

export const CHANNEL_IDS = Object.freeze({
    SMS: 1,
    WHATSAPP: 2,
    EMAIL: 3,
    VOICE: 4,
    IN_APP: 5,
    PUSH: 6,
    SMS_SEC: 7,
});

// Stored / API channel names — do not edit.
export const CHANNEL = Object.freeze({
    WHATSAPP: 'Whatsapp',
    SMS: 'SMS',
    PUSH: 'Push Notification',
    IN_APP: 'In-App Notification',
    VOICE: 'Voice (IVR)',
    EMAIL: 'E-Mail',
});

// Display order used by channel pickers and template tabs.
export const CHANNELS = Object.freeze([
    {
        id: CHANNEL_IDS.WHATSAPP,
        name: CHANNEL.WHATSAPP,
        label: 'WhatsApp',
        shortLabel: 'WhatsApp',
        icon: LuMessageCircle,
        color: '#00a63e',
        description: 'Rich messaging with media and buttons',
    },
    {
        id: CHANNEL_IDS.SMS,
        name: CHANNEL.SMS,
        label: 'SMS',
        shortLabel: 'SMS',
        icon: LuMessageSquare,
        color: '#155dfc',
        description: 'Simple text messages',
    },
    {
        id: CHANNEL_IDS.EMAIL,
        name: CHANNEL.EMAIL,
        label: 'E-Mail',
        shortLabel: 'Email',
        icon: LuMail,
        color: '#9810fa',
        description: 'HTML emails with rich content',
    },
    {
        id: CHANNEL_IDS.PUSH,
        name: CHANNEL.PUSH,
        label: 'Push Notification',
        shortLabel: 'Push',
        icon: LuBell,
        color: '#e60076',
        description: 'Mobile app notifications',
    },
    {
        id: CHANNEL_IDS.IN_APP,
        name: CHANNEL.IN_APP,
        label: 'Inbox',
        shortLabel: 'Inbox',
        icon: LuInbox,
        color: '#4f39f6',
        description: 'Inbox messages and alerts',
    },
    {
        id: CHANNEL_IDS.VOICE,
        name: CHANNEL.VOICE,
        label: 'Voice (IVR)',
        shortLabel: 'Voice',
        icon: LuPhone,
        color: '#f54a00',
        description: 'Automated voice calls',
    },
]);

// Spellings other endpoints use for the same channel (dashboards send 'WhatsApp',
// some reports send 'Email', short chart keys, etc.). Lower-cased for matching.
const NAME_ALIASES = {
    whatsapp: CHANNEL_IDS.WHATSAPP,
    sms: CHANNEL_IDS.SMS,
    'e-mail': CHANNEL_IDS.EMAIL,
    email: CHANNEL_IDS.EMAIL,
    'push notification': CHANNEL_IDS.PUSH,
    'push notifications': CHANNEL_IDS.PUSH,
    push: CHANNEL_IDS.PUSH,
    'in-app notification': CHANNEL_IDS.IN_APP,
    'in app notification': CHANNEL_IDS.IN_APP,
    'in-app': CHANNEL_IDS.IN_APP,
    inapp: CHANNEL_IDS.IN_APP,
    inbox: CHANNEL_IDS.IN_APP,
    'voice (ivr)': CHANNEL_IDS.VOICE,
    'voice / ivr': CHANNEL_IDS.VOICE,
    voice: CHANNEL_IDS.VOICE,
    ivr: CHANNEL_IDS.VOICE,
};

const BY_ID = new Map(CHANNELS.map(c => [c.id, c]));

// Accepts a numeric id, a numeric string ('5'), or any known name spelling.
export function getChannel(idOrName) {
    if (idOrName === null || idOrName === undefined || idOrName === '') return undefined;
    const asNumber = Number(idOrName);
    if (!Number.isNaN(asNumber) && BY_ID.has(asNumber)) return BY_ID.get(asNumber);
    if (typeof idOrName !== 'string') return undefined;
    const id = NAME_ALIASES[idOrName.trim().toLowerCase()];
    return id ? BY_ID.get(id) : undefined;
}

// UI label for a channel id/name. Unknown values are returned as-is so nothing
// the backend adds later disappears from the screen.
export function getChannelLabel(idOrName, { short = false } = {}) {
    const channel = getChannel(idOrName);
    if (!channel) return idOrName === null || idOrName === undefined ? '' : String(idOrName);
    return short ? channel.shortLabel : channel.label;
}

export function getChannelColor(idOrName, fallback = '#64748b') {
    return getChannel(idOrName)?.color || fallback;
}

export function getChannelIcon(idOrName, fallback = LuMessageSquare) {
    return getChannel(idOrName)?.icon || fallback;
}

// Merge local icon/colour/label onto API rows ({ id, name }) from /channel/list.
// The original `name` is kept untouched (it's still what gets sent back to the API).
export function withChannelMeta(rows) {
    return (rows || []).map(row => {
        const channel = getChannel(row?.id) || getChannel(row?.name);
        return channel
            ? { ...channel, ...row, label: channel.label, shortLabel: channel.shortLabel }
            : { ...row, label: row?.name, shortLabel: row?.name };
    });
}
